import { detectIntervalMinutes } from './fileParser';

/**
 * The project-level summary of an interval series: the numbers the AMI upload
 * writes onto the project (peak, when it happened, the interval, the period).
 *
 * One home for it, because three paths now write the same columns: the
 * contractor's upload (AMIDataInput.finishUpload), the homeowner's upload on
 * the authorization page, and the reconciliation that runs when a project is
 * opened holding readings but no peak. The numbers must agree wherever they
 * were computed, so they are computed here.
 */

export interface SeriesPoint {
  timestamp: string;
  kw: number;
}

export interface SeriesSummary {
  count: number;
  peakKw: number | null;
  /** Timestamp of the first reading that carries the peak, oldest first. */
  peakTimestamp: string | null;
  intervalMinutes: number | null;
  /** "Jan 1, 2025 - Dec 31, 2025", the same wording the report prints. */
  measurementPeriod: string;
  firstTimestamp: string | null;
  lastTimestamp: string | null;
}

/** Dates are fixed-UTC wall-clock instants (fileParser.utcWallClock), so format in UTC. */
function fmt(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function summarizeAmiSeries(points: SeriesPoint[]): SeriesSummary {
  if (points.length === 0) {
    return { count: 0, peakKw: null, peakTimestamp: null, intervalMinutes: null, measurementPeriod: '', firstTimestamp: null, lastTimestamp: null };
  }
  const sorted = [...points].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  let peakKw = -Infinity;
  let peakTimestamp: string | null = null;
  for (const p of sorted) {
    if (p.kw > peakKw) {
      peakKw = p.kw;
      peakTimestamp = p.timestamp;
    }
  }
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  return {
    count: sorted.length,
    peakKw: Number.isFinite(peakKw) ? peakKw : null,
    peakTimestamp,
    intervalMinutes: detectIntervalMinutes(sorted),
    measurementPeriod: `${fmt(new Date(first.timestamp))} - ${fmt(new Date(last.timestamp))}`,
    firstTimestamp: first.timestamp,
    lastTimestamp: last.timestamp,
  };
}
